// src/pages/finance-portal/fixed-assets/Operations.js
//
// Transfers, Maintenance, Physical Counts (tabbed view)

import React, { useCallback, useEffect, useState } from 'react';
import {
    Alert, Box, Button, Card, CardContent, Chip, CircularProgress, Dialog,
    DialogActions, DialogContent, DialogTitle, Grid, IconButton, MenuItem,
    Paper, Skeleton, Tab, Table, TableBody, TableCell, TableContainer,
    TableHead, TableRow, Tabs, TextField, Typography,
} from '@mui/material';
import EngineeringIcon from '@mui/icons-material/Engineering';
import AddIcon from '@mui/icons-material/Add';
import { useSnackbar } from 'notistack';

import axiosInstance from '../../../utils/axiosInstance';
import {
    FA_FONT, FA_ACCENT, FA_ACCENT_DARK,
    formatCurrency, formatDate, humanizeStatus,
} from './components/fixedAssetFormatters';

const headSx = { fontFamily: FA_FONT, fontWeight: 700, fontSize: '0.72rem', color: '#64748b', textTransform: 'uppercase', borderBottom: '2px solid #e2e8f0' };
const cellSx = { fontFamily: FA_FONT, fontSize: '0.84rem', color: '#1e293b', borderBottom: '1px solid #f1f5f9' };

const MAINTENANCE_TYPES = ['PREVENTIVE', 'CONDITION_BASED', 'CORRECTIVE', 'EMERGENCY', 'INSPECTION', 'UPGRADE', 'OTHER'];
const TRANSFER_REASONS = ['REORGANIZATION', 'REPAIR', 'DEPLOYMENT', 'DISPOSAL_PREP', 'LEASE', 'OTHER'];

export default function Operations() {
    const { enqueueSnackbar } = useSnackbar();
    const [tab, setTab] = useState(0);
    const [assets, setAssets] = useState([]);

    const [transfers, setTransfers] = useState([]);
    const [maintenance, setMaintenance] = useState([]);
    const [counts, setCounts] = useState([]);
    const [loading, setLoading] = useState(false);

    const [transferOpen, setTransferOpen] = useState(false);
    const [maintenanceOpen, setMaintenanceOpen] = useState(false);
    const [countOpen, setCountOpen] = useState(false);

    const [tForm, setTForm] = useState({
        asset_id: '', transfer_date: new Date().toISOString().slice(0, 10),
        to_location: '', to_department: '', reason: 'OTHER', reason_description: '',
    });
    const [tErrors, setTErrors] = useState({});
    const [mForm, setMForm] = useState({
        asset_id: '', maintenance_type: 'PREVENTIVE', maintenance_date: new Date().toISOString().slice(0, 10),
        description: '', cost: '', treatment: 'EXPENSE',
    });
    const [mErrors, setMErrors] = useState({});
    const [cForm, setCForm] = useState({
        name: '', scheduled_date: new Date().toISOString().slice(0, 10),
        scope_location: '', scope_department: '',
    });
    const [cErrors, setCErrors] = useState({});

    const setTField = (k, v) => {
        setTForm(prev => ({ ...prev, [k]: v }));
        if (tErrors[k]) setTErrors(prev => { const n = { ...prev }; delete n[k]; return n; });
    };
    const setMField = (k, v) => {
        setMForm(prev => ({ ...prev, [k]: v }));
        if (mErrors[k]) setMErrors(prev => { const n = { ...prev }; delete n[k]; return n; });
    };
    const setCField = (k, v) => {
        setCForm(prev => ({ ...prev, [k]: v }));
        if (cErrors[k]) setCErrors(prev => { const n = { ...prev }; delete n[k]; return n; });
    };

    const validateTransfer = () => {
        const e = {};
        if (!tForm.asset_id) e.asset_id = 'Select an asset';
        if (!tForm.transfer_date) e.transfer_date = 'Transfer date is required';
        if (!tForm.to_location && !tForm.to_department) {
            e.to_location = 'Provide at least a destination location or department';
        }
        return e;
    };

    const validateMaintenance = () => {
        const e = {};
        if (!mForm.asset_id) e.asset_id = 'Select an asset';
        if (!mForm.maintenance_date) e.maintenance_date = 'Maintenance date is required';
        if (mForm.cost !== '' && mForm.cost !== null) {
            const c = parseFloat(mForm.cost);
            if (Number.isNaN(c) || c < 0) e.cost = 'Cost must be a non-negative number';
        }
        if (mForm.treatment === 'CAPITALIZE' && (!mForm.cost || parseFloat(mForm.cost) <= 0)) {
            e.cost = 'Capitalized maintenance must have a positive cost';
        }
        return e;
    };

    const validateCount = () => {
        const e = {};
        if (!cForm.name || !cForm.name.trim()) e.name = 'Count name is required';
        if (!cForm.scheduled_date) e.scheduled_date = 'Scheduled date is required';
        return e;
    };

    const loadTab = useCallback(async () => {
        setLoading(true);
        try {
            if (tab === 0) {
                const r = await axiosInstance.get('/api/finance/fixed-assets/transfers',
                    { params: { per_page: 100 } });
                setTransfers(r.data?.data || []);
            } else if (tab === 1) {
                const r = await axiosInstance.get('/api/finance/fixed-assets/maintenance-records',
                    { params: { per_page: 100 } });
                setMaintenance(r.data?.data || []);
            } else if (tab === 2) {
                const r = await axiosInstance.get('/api/finance/fixed-assets/physical-counts',
                    { params: { per_page: 100 } });
                setCounts(r.data?.data || []);
            }
        } catch (e) {
            enqueueSnackbar(e.response?.data?.message || 'Load failed', { variant: 'error' });
        } finally {
            setLoading(false);
        }
    }, [tab, enqueueSnackbar]);

    useEffect(() => { loadTab(); }, [loadTab]);

    useEffect(() => {
        axiosInstance.get('/api/finance/fixed-assets/assets',
            { params: { status: 'ACTIVE', per_page: 200 } })
            .then(r => { if (r.data?.success) setAssets(r.data.data || []); })
            .catch(() => {});
    }, []);

    const submit = async (url, payload, dialogSetter, msg, validator, errorSetter) => {
        if (validator && errorSetter) {
            const errs = validator();
            if (Object.keys(errs).length > 0) {
                errorSetter(errs);
                enqueueSnackbar(`Fix ${Object.keys(errs).length} validation error(s)`, { variant: 'warning' });
                return;
            }
            errorSetter({});
        }
        try {
            const res = await axiosInstance.post(url, payload);
            if (res.data?.success) {
                enqueueSnackbar(msg, { variant: 'success' });
                dialogSetter(false);
                loadTab();
            }
        } catch (e) {
            const serverErrors = e.response?.data?.errors;
            if (serverErrors && typeof serverErrors === 'object' && errorSetter) {
                errorSetter(prev => ({ ...prev, ...serverErrors }));
                enqueueSnackbar(e.response?.data?.message || 'Validation failed on server',
                    { variant: 'warning' });
            } else {
                enqueueSnackbar(e.response?.data?.message || 'Failed', { variant: 'error' });
            }
        }
    };

    return (
        <Box sx={{ minHeight: '100vh', bgcolor: '#f8fafc' }}>
            <Paper elevation={0} sx={{
                background: `linear-gradient(135deg, ${FA_ACCENT} 0%, ${FA_ACCENT_DARK} 100%)`,
                borderRadius: { xs: 0, md: '0 0 24px 24px' },
                px: { xs: 2.5, md: 4 }, py: 3, mb: 3,
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <EngineeringIcon sx={{ fontSize: 30, color: '#fff' }} />
                    <Typography sx={{ fontFamily: FA_FONT, fontWeight: 800, color: 'white', fontSize: '1.6rem' }}>
                        Asset Operations
                    </Typography>
                </Box>
            </Paper>

            <Box sx={{ px: { xs: 2, md: 4 }, pb: 4, maxWidth: 1300, mx: 'auto' }}>
                <Card sx={{ borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: 'none' }}>
                    <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ borderBottom: '1px solid #e2e8f0', px: 2 }}
                        TabIndicatorProps={{ style: { backgroundColor: FA_ACCENT } }}>
                        <Tab label="Transfers" sx={{ fontFamily: FA_FONT, textTransform: 'none', fontWeight: 600 }} />
                        <Tab label="Maintenance" sx={{ fontFamily: FA_FONT, textTransform: 'none', fontWeight: 600 }} />
                        <Tab label="Physical Counts" sx={{ fontFamily: FA_FONT, textTransform: 'none', fontWeight: 600 }} />
                    </Tabs>

                    <CardContent sx={{ p: 3 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1.5 }}>
                            {tab === 0 && (
                                <Button startIcon={<AddIcon />} variant="contained" onClick={() => setTransferOpen(true)}
                                    sx={{ fontFamily: FA_FONT, textTransform: 'none', bgcolor: FA_ACCENT,
                                        '&:hover': { bgcolor: FA_ACCENT_DARK }, borderRadius: '10px' }}>
                                    New Transfer
                                </Button>
                            )}
                            {tab === 1 && (
                                <Button startIcon={<AddIcon />} variant="contained" onClick={() => setMaintenanceOpen(true)}
                                    sx={{ fontFamily: FA_FONT, textTransform: 'none', bgcolor: FA_ACCENT,
                                        '&:hover': { bgcolor: FA_ACCENT_DARK }, borderRadius: '10px' }}>
                                    Log Maintenance
                                </Button>
                            )}
                            {tab === 2 && (
                                <Button startIcon={<AddIcon />} variant="contained" onClick={() => setCountOpen(true)}
                                    sx={{ fontFamily: FA_FONT, textTransform: 'none', bgcolor: FA_ACCENT,
                                        '&:hover': { bgcolor: FA_ACCENT_DARK }, borderRadius: '10px' }}>
                                    New Count
                                </Button>
                            )}
                        </Box>

                        {tab === 0 && (
                            <TableContainer>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>{['Number', 'Date', 'Asset', 'From', 'To', 'Reason', 'Status'].map(h => (
                                            <TableCell key={h} sx={headSx}>{h}</TableCell>
                                        ))}</TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {loading ? [1, 2].map(i => (
                                            <TableRow key={i}>{[1, 2, 3, 4, 5, 6, 7].map(j => <TableCell key={j}><Skeleton /></TableCell>)}</TableRow>
                                        )) : transfers.length === 0 ? (
                                            <TableRow>
                                                <TableCell colSpan={7} sx={{ textAlign: 'center', py: 6 }}>
                                                    <Typography sx={{ fontFamily: FA_FONT, color: '#94a3b8' }}>No transfers</Typography>
                                                </TableCell>
                                            </TableRow>
                                        ) : transfers.map(t => (
                                            <TableRow key={t.id}>
                                                <TableCell sx={cellSx}>{t.transfer_number}</TableCell>
                                                <TableCell sx={cellSx}>{formatDate(t.transfer_date)}</TableCell>
                                                <TableCell sx={cellSx}>{t.asset_name}</TableCell>
                                                <TableCell sx={cellSx}>{t.from_location || t.from_department || '\u2014'}</TableCell>
                                                <TableCell sx={cellSx}>{t.to_location || t.to_department || '\u2014'}</TableCell>
                                                <TableCell sx={cellSx}>{humanizeStatus(t.reason)}</TableCell>
                                                <TableCell sx={cellSx}>
                                                    <Chip size="small" label={humanizeStatus(t.status)}
                                                        sx={{ fontFamily: FA_FONT, fontWeight: 600 }} />
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        )}

                        {tab === 1 && (
                            <TableContainer>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>{['Date', 'Asset', 'Type', 'Description', 'Cost', 'Treatment', 'Status'].map(h => (
                                            <TableCell key={h} sx={headSx}>{h}</TableCell>
                                        ))}</TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {loading ? [1, 2].map(i => (
                                            <TableRow key={i}>{[1, 2, 3, 4, 5, 6, 7].map(j => <TableCell key={j}><Skeleton /></TableCell>)}</TableRow>
                                        )) : maintenance.length === 0 ? (
                                            <TableRow>
                                                <TableCell colSpan={7} sx={{ textAlign: 'center', py: 6 }}>
                                                    <Typography sx={{ fontFamily: FA_FONT, color: '#94a3b8' }}>No maintenance records</Typography>
                                                </TableCell>
                                            </TableRow>
                                        ) : maintenance.map(m => (
                                            <TableRow key={m.id}>
                                                <TableCell sx={cellSx}>{formatDate(m.maintenance_date)}</TableCell>
                                                <TableCell sx={cellSx}>{m.asset_name}</TableCell>
                                                <TableCell sx={cellSx}>{humanizeStatus(m.maintenance_type)}</TableCell>
                                                <TableCell sx={cellSx}>{m.description || '\u2014'}</TableCell>
                                                <TableCell sx={cellSx}>{formatCurrency(m.cost)}</TableCell>
                                                <TableCell sx={cellSx}>{humanizeStatus(m.treatment)}</TableCell>
                                                <TableCell sx={cellSx}>{humanizeStatus(m.status)}</TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        )}

                        {tab === 2 && (
                            <TableContainer>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>{['Number', 'Name', 'Scheduled', 'Expected', 'Found', 'Missing', 'Status'].map(h => (
                                            <TableCell key={h} sx={headSx}>{h}</TableCell>
                                        ))}</TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {loading ? [1, 2].map(i => (
                                            <TableRow key={i}>{[1, 2, 3, 4, 5, 6, 7].map(j => <TableCell key={j}><Skeleton /></TableCell>)}</TableRow>
                                        )) : counts.length === 0 ? (
                                            <TableRow>
                                                <TableCell colSpan={7} sx={{ textAlign: 'center', py: 6 }}>
                                                    <Typography sx={{ fontFamily: FA_FONT, color: '#94a3b8' }}>No counts yet</Typography>
                                                </TableCell>
                                            </TableRow>
                                        ) : counts.map(c => (
                                            <TableRow key={c.id}>
                                                <TableCell sx={cellSx}>{c.count_number}</TableCell>
                                                <TableCell sx={cellSx}>{c.name}</TableCell>
                                                <TableCell sx={cellSx}>{formatDate(c.scheduled_date)}</TableCell>
                                                <TableCell sx={cellSx}>{c.expected_count}</TableCell>
                                                <TableCell sx={cellSx}>{c.found_count}</TableCell>
                                                <TableCell sx={cellSx}>{c.missing_count}</TableCell>
                                                <TableCell sx={cellSx}>{humanizeStatus(c.status)}</TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        )}
                    </CardContent>
                </Card>
            </Box>

            {/* Transfer Dialog */}
            <Dialog open={transferOpen} onClose={() => setTransferOpen(false)} maxWidth="sm" fullWidth
                PaperProps={{ sx: { borderRadius: '16px' } }}>
                <DialogTitle sx={{ fontFamily: FA_FONT, fontWeight: 700 }}>New Transfer</DialogTitle>
                <DialogContent>
                    <Grid container spacing={2} sx={{ mt: 0 }}>
                        <Grid item xs={12}>
                            <TextField fullWidth size="small" select label="Asset *" required value={tForm.asset_id}
                                onChange={e => setTField('asset_id', e.target.value)}
                                error={Boolean(tErrors.asset_id)} helperText={tErrors.asset_id || ' '}
                                InputProps={{ sx: { fontFamily: FA_FONT } }}
                                InputLabelProps={{ sx: { fontFamily: FA_FONT } }}>
                                <MenuItem value="" sx={{ fontFamily: FA_FONT }}>Select</MenuItem>
                                {assets.map(a => <MenuItem key={a.id} value={a.id} sx={{ fontFamily: FA_FONT }}>{a.asset_number} — {a.name}</MenuItem>)}
                            </TextField>
                        </Grid>
                        <Grid item xs={6}>
                            <TextField fullWidth size="small" type="date" label="Transfer Date *" required value={tForm.transfer_date}
                                onChange={e => setTField('transfer_date', e.target.value)}
                                error={Boolean(tErrors.transfer_date)} helperText={tErrors.transfer_date || ' '}
                                InputLabelProps={{ shrink: true, sx: { fontFamily: FA_FONT } }}
                                InputProps={{ sx: { fontFamily: FA_FONT } }} />
                        </Grid>
                        <Grid item xs={6}>
                            <TextField fullWidth size="small" select label="Reason" value={tForm.reason}
                                onChange={e => setTField('reason', e.target.value)}
                                helperText=" "
                                InputProps={{ sx: { fontFamily: FA_FONT } }}
                                InputLabelProps={{ sx: { fontFamily: FA_FONT } }}>
                                {TRANSFER_REASONS.map(r => <MenuItem key={r} value={r} sx={{ fontFamily: FA_FONT }}>{humanizeStatus(r)}</MenuItem>)}
                            </TextField>
                        </Grid>
                        <Grid item xs={6}>
                            <TextField fullWidth size="small" label="To Location" value={tForm.to_location}
                                onChange={e => setTField('to_location', e.target.value)}
                                error={Boolean(tErrors.to_location)} helperText={tErrors.to_location || ' '}
                                InputProps={{ sx: { fontFamily: FA_FONT } }}
                                InputLabelProps={{ sx: { fontFamily: FA_FONT } }} />
                        </Grid>
                        <Grid item xs={6}>
                            <TextField fullWidth size="small" label="To Department" value={tForm.to_department}
                                onChange={e => setTField('to_department', e.target.value)}
                                helperText=" "
                                InputProps={{ sx: { fontFamily: FA_FONT } }}
                                InputLabelProps={{ sx: { fontFamily: FA_FONT } }} />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField fullWidth size="small" multiline minRows={2} label="Reason Description"
                                value={tForm.reason_description}
                                onChange={e => setTField('reason_description', e.target.value)}
                                InputProps={{ sx: { fontFamily: FA_FONT } }}
                                InputLabelProps={{ sx: { fontFamily: FA_FONT } }} />
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={() => setTransferOpen(false)} sx={{ fontFamily: FA_FONT }}>Cancel</Button>
                    <Button variant="contained" onClick={() => submit('/api/finance/fixed-assets/transfers', tForm,
                        setTransferOpen, 'Transfer created', validateTransfer, setTErrors)}
                        sx={{ fontFamily: FA_FONT, textTransform: 'none', bgcolor: FA_ACCENT,
                            '&:hover': { bgcolor: FA_ACCENT_DARK }, borderRadius: '10px' }}>Create</Button>
                </DialogActions>
            </Dialog>

            {/* Maintenance Dialog */}
            <Dialog open={maintenanceOpen} onClose={() => setMaintenanceOpen(false)} maxWidth="sm" fullWidth
                PaperProps={{ sx: { borderRadius: '16px' } }}>
                <DialogTitle sx={{ fontFamily: FA_FONT, fontWeight: 700 }}>Log Maintenance</DialogTitle>
                <DialogContent>
                    <Grid container spacing={2} sx={{ mt: 0 }}>
                        <Grid item xs={12}>
                            <TextField fullWidth size="small" select label="Asset *" required value={mForm.asset_id}
                                onChange={e => setMField('asset_id', e.target.value)}
                                error={Boolean(mErrors.asset_id)} helperText={mErrors.asset_id || ' '}
                                InputProps={{ sx: { fontFamily: FA_FONT } }}
                                InputLabelProps={{ sx: { fontFamily: FA_FONT } }}>
                                <MenuItem value="" sx={{ fontFamily: FA_FONT }}>Select</MenuItem>
                                {assets.map(a => <MenuItem key={a.id} value={a.id} sx={{ fontFamily: FA_FONT }}>{a.asset_number} — {a.name}</MenuItem>)}
                            </TextField>
                        </Grid>
                        <Grid item xs={6}>
                            <TextField fullWidth size="small" select label="Type" value={mForm.maintenance_type}
                                onChange={e => setMField('maintenance_type', e.target.value)}
                                helperText=" "
                                InputProps={{ sx: { fontFamily: FA_FONT } }}
                                InputLabelProps={{ sx: { fontFamily: FA_FONT } }}>
                                {MAINTENANCE_TYPES.map(t => <MenuItem key={t} value={t} sx={{ fontFamily: FA_FONT }}>{humanizeStatus(t)}</MenuItem>)}
                            </TextField>
                        </Grid>
                        <Grid item xs={6}>
                            <TextField fullWidth size="small" type="date" label="Date *" required value={mForm.maintenance_date}
                                onChange={e => setMField('maintenance_date', e.target.value)}
                                error={Boolean(mErrors.maintenance_date)} helperText={mErrors.maintenance_date || ' '}
                                InputLabelProps={{ shrink: true, sx: { fontFamily: FA_FONT } }}
                                InputProps={{ sx: { fontFamily: FA_FONT } }} />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField fullWidth size="small" multiline minRows={2} label="Description"
                                value={mForm.description}
                                onChange={e => setMField('description', e.target.value)}
                                InputProps={{ sx: { fontFamily: FA_FONT } }}
                                InputLabelProps={{ sx: { fontFamily: FA_FONT } }} />
                        </Grid>
                        <Grid item xs={6}>
                            <TextField fullWidth size="small" type="number" label="Cost" value={mForm.cost}
                                onChange={e => setMField('cost', e.target.value)}
                                error={Boolean(mErrors.cost)} helperText={mErrors.cost || ' '}
                                inputProps={{ min: 0, step: '0.01' }}
                                InputProps={{ sx: { fontFamily: FA_FONT } }}
                                InputLabelProps={{ sx: { fontFamily: FA_FONT } }} />
                        </Grid>
                        <Grid item xs={6}>
                            <TextField fullWidth size="small" select label="Treatment" value={mForm.treatment}
                                onChange={e => setMField('treatment', e.target.value)}
                                helperText=" "
                                InputProps={{ sx: { fontFamily: FA_FONT } }}
                                InputLabelProps={{ sx: { fontFamily: FA_FONT } }}>
                                <MenuItem value="EXPENSE" sx={{ fontFamily: FA_FONT }}>Expense (Repair)</MenuItem>
                                <MenuItem value="CAPITALIZE" sx={{ fontFamily: FA_FONT }}>Capitalize (Improvement)</MenuItem>
                            </TextField>
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={() => setMaintenanceOpen(false)} sx={{ fontFamily: FA_FONT }}>Cancel</Button>
                    <Button variant="contained" onClick={() => submit('/api/finance/fixed-assets/maintenance-records',
                        { ...mForm, cost: parseFloat(mForm.cost) || 0 }, setMaintenanceOpen,
                        'Maintenance recorded', validateMaintenance, setMErrors)}
                        sx={{ fontFamily: FA_FONT, textTransform: 'none', bgcolor: FA_ACCENT,
                            '&:hover': { bgcolor: FA_ACCENT_DARK }, borderRadius: '10px' }}>Record</Button>
                </DialogActions>
            </Dialog>

            {/* Physical Count Dialog */}
            <Dialog open={countOpen} onClose={() => setCountOpen(false)} maxWidth="sm" fullWidth
                PaperProps={{ sx: { borderRadius: '16px' } }}>
                <DialogTitle sx={{ fontFamily: FA_FONT, fontWeight: 700 }}>New Physical Count</DialogTitle>
                <DialogContent>
                    <Grid container spacing={2} sx={{ mt: 0 }}>
                        <Grid item xs={12}>
                            <TextField fullWidth size="small" label="Name *" required value={cForm.name}
                                onChange={e => setCField('name', e.target.value)}
                                error={Boolean(cErrors.name)} helperText={cErrors.name || ' '}
                                InputProps={{ sx: { fontFamily: FA_FONT } }}
                                InputLabelProps={{ sx: { fontFamily: FA_FONT } }} />
                        </Grid>
                        <Grid item xs={6}>
                            <TextField fullWidth size="small" type="date" label="Scheduled Date *" required
                                value={cForm.scheduled_date}
                                onChange={e => setCField('scheduled_date', e.target.value)}
                                error={Boolean(cErrors.scheduled_date)}
                                helperText={cErrors.scheduled_date || ' '}
                                InputLabelProps={{ shrink: true, sx: { fontFamily: FA_FONT } }}
                                InputProps={{ sx: { fontFamily: FA_FONT } }} />
                        </Grid>
                        <Grid item xs={6}>
                            <TextField fullWidth size="small" label="Scope: Location" value={cForm.scope_location}
                                onChange={e => setCField('scope_location', e.target.value)}
                                helperText=" "
                                InputProps={{ sx: { fontFamily: FA_FONT } }}
                                InputLabelProps={{ sx: { fontFamily: FA_FONT } }} />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField fullWidth size="small" label="Scope: Department" value={cForm.scope_department}
                                onChange={e => setCField('scope_department', e.target.value)}
                                InputProps={{ sx: { fontFamily: FA_FONT } }}
                                InputLabelProps={{ sx: { fontFamily: FA_FONT } }} />
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={() => setCountOpen(false)} sx={{ fontFamily: FA_FONT }}>Cancel</Button>
                    <Button variant="contained" onClick={() => submit('/api/finance/fixed-assets/physical-counts',
                        cForm, setCountOpen, 'Physical count created', validateCount, setCErrors)}
                        sx={{ fontFamily: FA_FONT, textTransform: 'none', bgcolor: FA_ACCENT,
                            '&:hover': { bgcolor: FA_ACCENT_DARK }, borderRadius: '10px' }}>Create</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
